import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, Upload, GraduationCap } from "lucide-react";
import { API } from "@/lib/auth";

const FIELDS_TOP = [
  { k: "eyebrow", l: "Eyebrow (small line above headline)" },
  { k: "title_line_1", l: "Headline — first line" },
  { k: "title_line_2", l: "Headline — italic accent line" },
  { k: "subhead", l: "Sub-headline paragraph", area: true },
  { k: "offer_line", l: "Welcome offer explainer line", area: true },
  { k: "hero_card_tag", l: "Overlay card tag (e.g. Free forever)" },
  { k: "hero_card_title", l: "Overlay card headline" },
  { k: "cta_form_button", l: "Signup button label" },
  { k: "consent_note", l: "Consent line under signup", area: true },
];

const FIELDS_PILLARS = [
  { k: "pillars_heading_1", l: "Pillars heading — first line" },
  { k: "pillars_heading_2", l: "Pillars heading — italic line" },
];

const FIELDS_GIFT = [
  { k: "gift_eyebrow", l: "Gift block eyebrow" },
  { k: "gift_title_1", l: "Gift block title — first line" },
  { k: "gift_title_2", l: "Gift block title — italic line" },
  { k: "gift_body", l: "Gift block description", area: true },
  { k: "gift_price_original", l: "Original price (e.g. £6.99)" },
  { k: "gift_price_now", l: "New price (e.g. £0)" },
  { k: "gift_cta_primary", l: "Primary CTA label" },
  { k: "gift_cta_secondary", l: "Secondary CTA label" },
];

const FIELDS_SUCCESS = [
  { k: "success_title", l: "Success card title" },
  { k: "success_body", l: "Success card body", area: true },
  { k: "success_button", l: "Success card button label" },
];

export default function FreeEducationContentEditor() {
  const [content, setContent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/site/content`);
      const fe = r.data?.free_education || {};
      setContent(fe);
      setDraft({
        ...fe,
        pillars: Array.isArray(fe.pillars) ? fe.pillars : [],
      });
    } catch (e) {
      toast.error("Could not load Free Education content");
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(content);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/site/content/free_education`, { data: draft });
      toast.success("Saved — refresh /free-education to see it live.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (fieldKey, files) => {
    const file = files?.[0];
    if (!file) return;
    setUploadingKey(fieldKey);
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Re-use the existing pamper/hero image uploader — returns { image: url }
      const r = await axios.post(`${API}/admin/upload/pamper`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.image || r.data?.url;
      if (!url) throw new Error("No URL returned");
      setDraft((d) => ({ ...d, [fieldKey]: url }));
      toast.success("Image uploaded — remember to save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const updatePillar = (idx, patch) => {
    setDraft((d) => {
      const next = [...(d.pillars || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...d, pillars: next };
    });
  };
  const addPillar = () => {
    setDraft((d) => ({ ...d, pillars: [...(d.pillars || []), { title: "New pillar", body: "Describe the value here." }] }));
  };
  const removePillar = (idx) => {
    setDraft((d) => ({ ...d, pillars: (d.pillars || []).filter((_, i) => i !== idx) }));
  };

  if (!draft) {
    return <div className="p-10 text-center text-[hsl(var(--onyx)/0.5)]">Loading Free Education content…</div>;
  }

  const renderField = (f) => (
    <div key={f.k}>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{f.l}</label>
      {f.area ? (
        <textarea
          value={draft[f.k] || ""}
          onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
          data-testid={`fe-field-${f.k}`}
          rows={3}
          className="mt-1.5 w-full rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 py-3 text-sm outline-none focus:border-[hsl(var(--onyx))]"
        />
      ) : (
        <input
          value={draft[f.k] || ""}
          onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
          data-testid={`fe-field-${f.k}`}
          className="mt-1.5 w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
        />
      )}
    </div>
  );

  const renderImage = (key, label) => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <div className="mt-1.5 flex flex-wrap items-center gap-3">
        {draft[key] ? (
          <img src={draft[key]} alt={label} className="w-32 h-40 object-cover rounded-2xl border border-[hsl(var(--onyx)/0.08)]" />
        ) : (
          <div className="w-32 h-40 rounded-2xl border border-dashed border-[hsl(var(--onyx)/0.15)] flex items-center justify-center text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.35)] text-center px-2">
            Using default
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label className="btn-pill btn-outline !text-xs cursor-pointer" data-testid={`fe-upload-${key}`}>
            <Upload size={12} />
            {uploadingKey === key ? "Uploading…" : draft[key] ? "Replace image" : "Upload image"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(key, e.target.files)} />
          </label>
          {draft[key] && (
            <button
              onClick={() => setDraft({ ...draft, [key]: "" })}
              className="text-xs text-[hsl(var(--onyx)/0.5)] hover:text-[hsl(var(--onyx))] underline text-left"
              data-testid={`fe-clear-${key}`}
            >
              Use default image
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-free-education-panel">
      <div className="rounded-3xl bg-[hsl(var(--blush)/0.5)] border border-[hsl(var(--gold)/0.4)] p-5 flex items-start gap-3">
        <GraduationCap size={20} className="text-[hsl(var(--gold))] mt-0.5" />
        <div className="text-sm text-[hsl(var(--onyx)/0.8)] leading-relaxed">
          Edit every headline, paragraph and image on the <strong>/free-education</strong> landing page. Changes go live immediately after saving.
        </div>
      </div>

      {/* Hero + form */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Hero &amp; signup</div>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS_TOP.map(renderField)}
        </div>
        {renderImage("hero_image", "Hero image (right column)")}
      </div>

      {/* Pillars */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="overline">"What you'll get" pillars</div>
          <button onClick={addPillar} className="btn-pill btn-outline !text-xs" data-testid="fe-add-pillar">
            <Plus size={12} /> Add pillar
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS_PILLARS.map(renderField)}
        </div>
        <div className="space-y-3">
          {(draft.pillars || []).map((p, idx) => (
            <div key={idx} className="rounded-2xl border border-[hsl(var(--onyx)/0.08)] p-4 space-y-3" data-testid={`fe-pillar-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Pillar {idx + 1}</span>
                <button onClick={() => removePillar(idx)} className="text-[hsl(var(--onyx)/0.5)] hover:text-[hsl(var(--burgundy))]" data-testid={`fe-remove-pillar-${idx}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <input
                value={p.title || ""}
                onChange={(e) => updatePillar(idx, { title: e.target.value })}
                placeholder="Pillar title"
                className="w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm"
                data-testid={`fe-pillar-title-${idx}`}
              />
              <textarea
                value={p.body || ""}
                onChange={(e) => updatePillar(idx, { body: e.target.value })}
                placeholder="Short description"
                rows={2}
                className="w-full rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 py-3 text-sm"
                data-testid={`fe-pillar-body-${idx}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Gift block */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Gift block (dark section)</div>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS_GIFT.map(renderField)}
        </div>
        {renderImage("gift_image", "Gift block image")}
      </div>

      {/* Success card */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Success card (after signup)</div>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS_SUCCESS.map(renderField)}
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 z-10 flex items-center justify-end gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="fe-save"
          className="btn-pill btn-primary disabled:opacity-50 shadow-lg"
        >
          <Save size={14} />
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}
